/**
 * ForecastingFilterContext
 * Multi-select filter state scoped to the Forecasting module.
 * URL-synced via useURLFilters with prefixed keys to avoid collision.
 */
import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useURLFilters, ParamValue } from '../../hooks/useURLFilters';

// ---- Types ----

export interface ForecastingDateRange {
  start: Date | null;
  end: Date | null;
}

export interface ForecastingFilterContextType {
  brands: string[];
  categories: string[];
  stores: string[];
  cities: string[];
  dateRange: ForecastingDateRange;

  setBrands: (v: string[]) => void;
  setCategories: (v: string[]) => void;
  setStores: (v: string[]) => void;
  setCities: (v: string[]) => void;
  setDateRange: (r: ForecastingDateRange) => void;
  clearAll: () => void;

  activeFilterCount: number;
}

// ---- Helpers ----

const PARAM_KEYS = ['f_brands', 'f_categories', 'f_stores', 'f_cities', 'f_from', 'f_to'];

function toStringArray(v: ParamValue): string[] {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.length > 0) return [v];
  return [];
}

function toDate(v: ParamValue): Date | null {
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// ---- Context ----

const ForecastingFilterCtx = createContext<ForecastingFilterContextType | undefined>(undefined);

export const ForecastingFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { params, setParam, clearAll: urlClearAll } = useURLFilters({ paramKeys: PARAM_KEYS, debounceMs: 300 });

  const brands = useMemo(() => toStringArray(params.f_brands), [params.f_brands]);
  const categories = useMemo(() => toStringArray(params.f_categories), [params.f_categories]);
  const stores = useMemo(() => toStringArray(params.f_stores), [params.f_stores]);
  const cities = useMemo(() => toStringArray(params.f_cities), [params.f_cities]);
  const dateRange = useMemo<ForecastingDateRange>(() => ({
    start: toDate(params.f_from),
    end: toDate(params.f_to),
  }), [params.f_from, params.f_to]);

  const setBrands = useCallback((v: string[]) => setParam('f_brands', v.length > 0 ? v : null), [setParam]);
  const setCategories = useCallback((v: string[]) => setParam('f_categories', v.length > 0 ? v : null), [setParam]);
  const setStores = useCallback((v: string[]) => setParam('f_stores', v.length > 0 ? v : null), [setParam]);
  const setCities = useCallback((v: string[]) => setParam('f_cities', v.length > 0 ? v : null), [setParam]);
  const setDateRange = useCallback((r: ForecastingDateRange) => {
    setParam('f_from', r.start);
    setParam('f_to', r.end);
  }, [setParam]);

  // When cities change, clear stores that no longer match (cascading handled by the bar component)
  // No action needed here — the bar component filters store options by selected cities.

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (brands.length > 0) count++;
    if (categories.length > 0) count++;
    if (stores.length > 0) count++;
    if (cities.length > 0) count++;
    if (dateRange.start || dateRange.end) count++;
    return count;
  }, [brands, categories, stores, cities, dateRange]);

  const value = useMemo<ForecastingFilterContextType>(() => ({
    brands, categories, stores, cities, dateRange,
    setBrands, setCategories, setStores, setCities, setDateRange,
    clearAll: urlClearAll,
    activeFilterCount,
  }), [brands, categories, stores, cities, dateRange, setBrands, setCategories, setStores, setCities, setDateRange, urlClearAll, activeFilterCount]);

  return (
    <ForecastingFilterCtx.Provider value={value}>
      {children}
    </ForecastingFilterCtx.Provider>
  );
};

export function useForecastingFilters(): ForecastingFilterContextType {
  const ctx = useContext(ForecastingFilterCtx);
  if (!ctx) {
    throw new Error('useForecastingFilters must be used within <ForecastingFilterProvider>');
  }
  return ctx;
}

export default ForecastingFilterProvider;
